import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../cart/useCart";
import { useSiteConfig } from "../config/SiteConfigContext";
import { apiFetch, ApiError } from "../api/client";
import { paymentErrorKey } from "../api/paymentError";
import { useIsMobile } from "../hooks/useIsMobile";
import { isValidPhone } from "../lib/contactValidation";
import { formatEuro } from "../lib/format";
import { ga4 } from "../analytics/ga4";
import { pathFor } from "../app/paths";
import { analyticsIds } from "../analytics/tracker";
import { API_BASE } from "../api/base";
import {
  clearCheckoutIntent,
  intentFor,
  markBeginCheckout,
  pendingCheckout,
  purchaseFingerprint,
  rememberOrder,
  type CheckoutConfig,
  type CheckoutIntent,
  type PaymentState,
} from "../checkout/checkoutState";

/**
 * I codici VAT che parlano del numero inserito, e non del servizio che lo verifica.
 *
 * Il backend ne restituisce sette, e sono due famiglie diverse travestite da una:
 * questi quattro dicono «questo numero non va», gli altri tre (`vat_unavailable`,
 * `vat_pending_timeout`, `vat_verification_failed`) dicono «non sono riuscito a
 * chiederlo al registro». Solo la prima famiglia deve segnare il campo in rosso:
 * per la seconda il cliente non ha sbagliato niente e non c'è niente da correggere.
 */
const VAT_ERRORI_DEL_NUMERO = new Set([
  "vat_required",
  "vat_invalid",
  "vat_not_german",
  "vat_unverified",
]);

/** Errori del servizio di verifica, non del numero: si riprova, con attesa crescente. */
const VAT_ERRORI_DEL_SERVIZIO = new Set(["vat_unavailable", "vat_pending_timeout", "vat_verification_failed"]);
const VAT_RETRY_DELAYS_S = [5, 15, 30];

interface CreatedOrder {
  id: string;
  order_number: number | null;
  status: string;
  total_cents: number;
  payment_state?: PaymentState;
}

export function Checkout() {
  const { locale } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { items, totalCents, clearCart, removeItem, advancePayment, quote } = useCart();
  const isMobile = useIsMobile();
  const cfg = useSiteConfig();

  const [submitting, setSubmitting] = useState(false);
  // Guardia SINCRONA contro il doppio invio: lo stato React si aggiorna solo al
  // render successivo, quindi due click nello stesso tick lo leggevano entrambi
  // `false` e creavano due ordini (R-63).
  const submittingRef = useRef(false);
  const [error, setError] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [removedItems, setRemovedItems] = useState<string[]>([]);
  const [config, setConfig] = useState<CheckoutConfig | null>(null);
  const [pending, setPending] = useState<CheckoutIntent | null>(() => pendingCheckout());
  const [resuming, setResuming] = useState(false);
  // Fase dell'invio, per la riga di stato: prima la verifica della P.IVA e la
  // creazione dell'ordine, poi l'apertura del pagamento (prima la riga diceva
  // «verifica della partita IVA» anche mentre si apriva Stripe, R-79).
  const [phase, setPhase] = useState<"order" | "payment">("order");
  // Riprova controllata della verifica P.IVA quando il registro non risponde.
  const [vatRetryAt, setVatRetryAt] = useState<number | null>(null);
  const [vatFailures, setVatFailures] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  // Validate cart items on mount — remove any that no longer exist
  useEffect(() => {
    if (items.length === 0) return;
    fetch(`${API_BASE}/api/v1/shop/cart/validate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
      }),
    })
      .then((r) => r.json())
      .then((data: { invalid?: string[] }) => {
        if (data.invalid && data.invalid.length > 0) {
          const removed: string[] = [];
          for (const id of data.invalid) {
            const item = items.find((i) => i.id === id);
            if (item) removed.push(item.title);
            removeItem(id);
          }
          setRemovedItems(removed);
        }
      })
      .catch(() => { /* ignore validation errors */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConfig = useCallback(async () => {
    try {
      setConfig(await apiFetch<CheckoutConfig>("/api/v1/shop/checkout-config"));
    } catch {
      setConfig(null); // backend precedente: si procede come prima
    }
  }, []);

  // Contratto sulla modalità di pagamento: il checkout mostra ciò che il server
  // farà davvero (pagamento online o richiesta) e lo dichiara a ogni ordine.
  useEffect(() => {
    let active = true;
    apiFetch<CheckoutConfig>("/api/v1/shop/checkout-config")
      .then((c) => { if (active) setConfig(c); })
      .catch(() => { /* backend precedente: si procede come prima */ });
    return () => { active = false; };
  }, []);

  // «Indietro» dalla pagina di pagamento: il browser può restituire questa
  // pagina dalla cache (bfcache) con il pulsante fermo su «Elaborazione…». Si
  // riparte puliti e si propone di riprendere l'ordine già creato (R-72).
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      submittingRef.current = false;
      setSubmitting(false);
      setResuming(false);
      setPending(pendingCheckout());
    };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  // Conto alla rovescia della riprova P.IVA, solo mentre serve.
  useEffect(() => {
    if (vatRetryAt === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [vatRetryAt]);
  const vatRetrySeconds = vatRetryAt === null ? 0 : Math.max(0, Math.ceil((vatRetryAt - now) / 1000));

  const [shipping, setShipping] = useState({
    // VAT come primo campo: solo aziende tedesche con P.IVA valida possono acquistare.
    vat_number: "",
    shipping_name: "",
    shipping_address: "",
    shipping_city: "",
    shipping_postal_code: "",
    // Paese bloccato sulla Germania (input read-only). Il backend forza comunque
    // "Germany" a prescindere dal valore inviato: non aggirabile dal browser.
    shipping_country: "Germany",
    shipping_phone: "",
    notes: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});

  // Normalizzazione VAT lato client (solo per il feedback UX; la verifica reale
  // avviene lato backend via Stripe/VIES). Uppercase + rimozione separatori.
  const normalizeVat = (v: string) => v.replace(/[\s.\-_/]/g, "").toUpperCase();
  const DE_VAT_RE = /^DE\d{9}$/;

  // Formato italiano condiviso "1.234,56 €" (simbolo incluso → nessun &euro; a parte).
  const formatPrice = (cents: number) => formatEuro(cents);

  const updateField = (field: string, value: string) => {
    setShipping((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handlePlaceOrder = async () => {
    if (items.length === 0) return;

    const required = ["shipping_name", "shipping_address", "shipping_city", "shipping_postal_code", "shipping_country", "shipping_phone"];
    const errors: Record<string, boolean> = {};
    let vatMsg = "";
    // VAT (primo campo, obbligatorio): pre-check di formato per feedback immediato.
    // Il blocco REALE resta lato backend (verifica Stripe/VIES).
    const vat = normalizeVat(shipping.vat_number);
    if (!vat) {
      errors["vat_number"] = true;
      vatMsg = t("checkout.vat.errors.vat_required");
    } else if (!DE_VAT_RE.test(vat)) {
      errors["vat_number"] = true;
      vatMsg = t("checkout.vat.errors.vat_invalid");
    }
    for (const field of required) {
      const val = shipping[field as keyof typeof shipping].trim();
      if (!val) {
        errors[field] = true;
      } else if (val.length > 200) {
        errors[field] = true;
      }
    }
    // Postal code: 3-10 alphanumeric
    const pc = shipping.shipping_postal_code.trim();
    if (pc && !/^[a-zA-Z0-9\s-]{3,10}$/.test(pc)) {
      errors["shipping_postal_code"] = true;
    }
    // Phone: obbligatorio (già in `required`) e, se presente, deve essere valido.
    // Stessa validazione condivisa dei form contatti (lib/contactValidation).
    const phone = shipping.shipping_phone.trim();
    if (phone && !isValidPhone(phone)) {
      errors["shipping_phone"] = true;
    }
    // Name: no HTML/script tags
    const name = shipping.shipping_name.trim();
    if (name && /<[^>]+>/.test(name)) {
      errors["shipping_name"] = true;
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Il messaggio VAT è il più preciso e ha la precedenza. Se però a mancare è
      // un altro campo, senza ripiego resterebbe la stringa vuota: il riquadro
      // dell'errore non viene reso affatto e premere «Conferma ordine» sembra non
      // fare niente. Su schermo stretto il campo in rosso può essere fuori dalla
      // parte visibile, quindi non resta nessun segnale che dica cosa manca.
      setError(vatMsg || t("checkout.errors.missingFields"));
      return;
    }

    // Evita doppi invii: la ref è sincrona (due click nello stesso tick si
    // fermano qui), lo stato serve solo a disegnare il pulsante.
    if (submittingRef.current || vatRetrySeconds > 0) return;
    submittingRef.current = true;
    setSubmitting(true);
    setPhase("order");
    setError("");

    // La modalità che la pagina sta mostrando: se sul server è cambiata, la
    // richiesta è rifiutata prima di creare qualsiasi cosa.
    const mode = config?.mode ?? "online";
    // Stessi campi e stessa normalizzazione dell'impronta del server: lo stesso
    // acquisto ritentato porta la stessa chiave e ritrova lo stesso ordine.
    const fingerprint = purchaseFingerprint({
      items: items.map((i) => [i.id, i.quantity]).sort(),
      advance_payment: !!advancePayment,
      discount_code: discountCode.trim().toUpperCase() || null,
      vat_number: vat || null,
      shipping: [
        shipping.shipping_name, shipping.shipping_address, shipping.shipping_city,
        shipping.shipping_postal_code, shipping.shipping_phone,
      ].map((v) => v.trim()),
      notes: shipping.notes.trim() || null,
    });
    const createOrder = (key: string) => apiFetch<CreatedOrder>("/api/v1/shop/orders", {
      method: "POST",
      headers: { "Idempotency-Key": key, "X-Checkout-Mode": mode },
      // Gli identificatori analytics viaggiano già qui: anche un ordine che a
      // Stripe non arriva (errore del provider, scheda chiusa) conserva la provenienza.
      body: JSON.stringify({
        items: items.map((item) => ({ product_id: item.id, quantity: item.quantity })),
        advance_payment: advancePayment,
        discount_code: discountCode.trim() || null,
        ...shipping,
        locale,
        ...analyticsIds(),
      }),
    });
    let orderId: string | null = null;

    try {
      // 1. Crea l'ordine, o ritrova quello già creato da questo stesso acquisto.
      let order = await createOrder(intentFor(fingerprint).key);
      if (order.status === "CANCELLED") {
        // L'ordine di questo intento è stato chiuso nel frattempo (pulizia o
        // operatore): l'intento è finito, se ne apre uno nuovo con chiave nuova.
        // Senza id: l'intento appena usato non ha ancora l'ordine registrato.
        clearCheckoutIntent();
        order = await createOrder(intentFor(fingerprint).key);
      }
      orderId = order.id;
      rememberOrder(fingerprint, order.id, order.order_number);
      setVatFailures(0);
      setVatRetryAt(null);
      // GA4 begin_checkout: emesso solo quando l'ordine esiste davvero lato server
      // (valore = total_cents del server, lordo IVA), e una sola volta per
      // intento: un ritentativo dello stesso acquisto non è un nuovo checkout.
      const trackBeginCheckout = () => {
        if (!markBeginCheckout(fingerprint)) return;
        ga4.beginCheckout(
          items.map((i) => ({ item_id: i.slug || i.id, item_name: i.title, price: i.price_cents / 100, quantity: i.quantity })),
          order.total_cents,
          items[0]?.currency || "EUR",
        );
      };

      // 1b. Già confermato (sconto 100% → €0, o pagato da un'altra scheda) oppure
      // richiesta d'ordine in modalità manuale: niente Stripe, pagina di esito.
      const confirmed = ["PAID", "SHIPPED", "DELIVERED"].includes(order.status);
      if (order.total_cents <= 0 || confirmed || order.payment_state === "request_received") {
        trackBeginCheckout();
        clearCart();
        clearCheckoutIntent(order.id);
        navigate(`/${locale}/checkout/success?order_id=${order.id}`);
        return;
      }
      setPhase("payment");
      // 2. Apre (o riprende) il pagamento online di quell'ordine. Gli
      // identificatori analytics viaggiano anche qui: il webhook arriva dai
      // server di Stripe e non può sapere da quale scheda fosse partito l'acquisto.
      const session = await apiFetch<{ checkout_url: string }>(
        "/api/v1/payments/stripe/checkout-session",
        { method: "POST", body: JSON.stringify({ order_id: order.id, locale, ...analyticsIds() }) },
      );
      // 3. Reindirizza a Stripe. Il carrello resta finché il pagamento non è
      // confermato (pagina di esito): annullando, il cliente lo ritrova (R-72).
      trackBeginCheckout();
      window.location.assign(session.checkout_url);
    } catch (err: unknown) {
      // Codice leggibile: `detail.code` per i pagamenti, `detail` stringa per VAT e sconti.
      const code = err instanceof ApiError ? (err.code ?? err.message) : "";
      if (code === "already_paid" && orderId) {
        // Pagato nel frattempo (altra scheda): la pagina di esito lo verifica.
        navigate(`/${locale}/checkout/success?order_id=${orderId}`);
        return;
      }
      if (code === "checkout_mode_mismatch") {
        // La modalità è cambiata sul server: si rilegge e si mostra quella vera.
        // Nessun ordine è stato creato, quindi riprovare è sicuro.
        void loadConfig();
        setError(t("checkout.errors.modeChanged"));
      }
      // Errore VAT (422 vat_*): messaggio professionale dedicato, mai il codice grezzo.
      else if (code.startsWith("vat_")) {
        const key = `checkout.vat.errors.${code}`;
        const msg = t(key);
        setError(msg === key ? t("checkout.vat.errors.generic") : msg);
        // Il bordo rosso accusa il campo, quindi si accende solo quando è davvero
        // il numero a non andare. Se il registro europeo non ha saputo rispondere,
        // il dato del cliente è a posto e segnarlo come sbagliato lo manda a
        // ricontrollare una partita IVA corretta — che è esattamente quello che
        // non deve fare: deve solo riprovare.
        if (VAT_ERRORI_DEL_NUMERO.has(code)) {
          setFormErrors((prev) => ({ ...prev, vat_number: true }));
        } else if (VAT_ERRORI_DEL_SERVIZIO.has(code)) {
          // Riprova controllata, con attesa crescente: niente raffica di
          // richieste verso un registro che già non risponde.
          const delay = VAT_RETRY_DELAYS_S[Math.min(vatFailures, VAT_RETRY_DELAYS_S.length - 1)];
          setVatFailures((n) => n + 1);
          setNow(Date.now());
          setVatRetryAt(Date.now() + delay * 1000);
        }
      }
      // Codice sconto non valido (422 discount_*): messaggio dedicato.
      else if (code.startsWith("discount_")) {
        setError(t("checkout.discountInvalid"));
      } else {
        // Messaggio localizzato per codice o status; mai la stringa tecnica del backend.
        setError(t(paymentErrorKey(err)));
      }
      // Ordine creato ma pagamento non aperto (provider giù, rete): si può
      // riprendere da qui senza crearne un altro.
      if (orderId) setPending(pendingCheckout());
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  /** Riprende il pagamento dell'ordine già creato da questa scheda (R-72). */
  const handleResume = async () => {
    const current = pendingCheckout();
    if (!current?.orderId || submittingRef.current) return;
    const resumeId = current.orderId;
    submittingRef.current = true;
    setResuming(true);
    setError("");
    try {
      const session = await apiFetch<{ checkout_url: string }>(
        "/api/v1/payments/stripe/checkout-session",
        { method: "POST", body: JSON.stringify({ order_id: resumeId, locale, ...analyticsIds() }) },
      );
      window.location.assign(session.checkout_url);
    } catch (err: unknown) {
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === "already_paid") {
        navigate(`/${locale}/checkout/success?order_id=${resumeId}`);
        return;
      }
      if (code === "order_cancelled" || code === "order_not_found" || code === "not_payable") {
        // Quell'ordine non si può più pagare: si dimentica, il carrello resta.
        clearCheckoutIntent(resumeId);
        setPending(null);
      }
      setError(t(paymentErrorKey(err)));
      submittingRef.current = false;
      setResuming(false);
    }
  };

  const inputStyle = (field: string): React.CSSProperties => ({
    width: "100%",
    border: formErrors[field] ? "1px solid #ef4444" : "1px solid rgba(28,75,59,0.89)",
    borderRadius: 8,
    padding: "11px 14px",
    fontSize: 14,
    boxSizing: "border-box",
    outline: "none",
    background: cfg.color_light_bg,
    transition: "border-color 0.2s",
  });

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 12,
    fontWeight: 500,
    marginBottom: 4,
    color: cfg.color_dark,
    letterSpacing: 0.2,
  };

  const overline: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: 2,
    color: cfg.color_muted,
    marginBottom: 16,
  };

  return (
    <div style={{ padding: isMobile ? "32px 20px 64px" : "64px 24px 96px", maxWidth: 960, margin: "0 auto" }}>
      {removedItems.length > 0 && (
        <div style={{
          background: "#fef3c7",
          border: "1px solid #f59e0b",
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 24,
          fontSize: 13,
          color: "#92400e",
          lineHeight: 1.5,
        }}>
          {t("checkout.removedItems")} <strong>{removedItems.join(", ")}</strong>
        </div>
      )}
      {pending && config?.mode !== "manual" && (
        <div role="status" data-testid="pending-payment" style={{
          background: "#eef6f1",
          border: `1px solid ${cfg.color_primary}`,
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 24,
          fontSize: 13,
          color: cfg.color_dark,
          lineHeight: 1.5,
        }}>
          <strong>{t("checkout.pendingPayment.title")}</strong>
          <p style={{ margin: "4px 0 10px" }}>
            {t("checkout.pendingPayment.body", { number: pending.orderNumber ?? "" })}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={handleResume}
              disabled={resuming || submitting}
              style={{ background: cfg.color_primary, color: "#fff", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 13, cursor: "pointer", opacity: resuming ? 0.7 : 1 }}
            >
              {resuming ? t("checkout.processing") : t("checkout.pendingPayment.resume")}
            </button>
            {/* Nasconde soltanto: l'intento resta, e lo stesso acquisto ritentato
                ritrova questo ordine invece di crearne un secondo. */}
            <button
              type="button"
              onClick={() => setPending(null)}
              style={{ background: "transparent", color: cfg.color_dark, border: "none", padding: "8px 4px", fontSize: 13, cursor: "pointer", textDecoration: "underline" }}
            >
              {t("checkout.pendingPayment.dismiss")}
            </button>
          </div>
        </div>
      )}
      <div style={overline}>{t("checkout.title")}</div>
      <h1 style={{
        fontFamily: cfg.font_heading,
        fontSize: isMobile ? 28 : 36,
        fontWeight: 400,
        color: cfg.color_dark,
        margin: "0 0 48px 0",
        lineHeight: 1.2,
      }}>
        {t("checkout.title")}
      </h1>

      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 360px",
        gap: isMobile ? 32 : 48,
        alignItems: "start",
      }}>
        {/* Shipping form */}
        <div style={{
          background: cfg.color_light_bg,
          borderRadius: 8,
          padding: isMobile ? "24px 20px" : "32px",
        }}>
          <div style={{ ...overline, marginBottom: 24 }}>{t("checkout.shippingTitle")}</div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* VAT — primo campo, obbligatorio. Solo aziende tedesche con P.IVA valida. */}
            <div>
              <label htmlFor="co-vat" style={labelStyle}>{t("checkout.vat.label")} *</label>
              <input
                type="text"
                id="co-vat"
                value={shipping.vat_number}
                onChange={(e) => updateField("vat_number", e.target.value)}
                onBlur={(e) => updateField("vat_number", normalizeVat(e.target.value))}
                style={inputStyle("vat_number")}
                placeholder={t("checkout.vat.placeholder")}
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
              />
              <p style={{ fontSize: 11, color: formErrors.vat_number ? "#ef4444" : cfg.color_muted, marginTop: 4, lineHeight: 1.4 }}>
                {t("checkout.vat.help")}
              </p>
            </div>
            <div>
              <label htmlFor="co-name" style={labelStyle}>{t("checkout.shippingName")} *</label>
              <input
                type="text"
                id="co-name"
                value={shipping.shipping_name}
                onChange={(e) => updateField("shipping_name", e.target.value)}
                style={inputStyle("shipping_name")}
                placeholder={t("checkout.shippingNamePh")}
              />
            </div>
            <div>
              <label htmlFor="co-address" style={labelStyle}>{t("checkout.shippingAddress")} *</label>
              <input
                type="text"
                id="co-address"
                value={shipping.shipping_address}
                onChange={(e) => updateField("shipping_address", e.target.value)}
                style={inputStyle("shipping_address")}
                placeholder={t("checkout.shippingAddressPh")}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label htmlFor="co-city" style={labelStyle}>{t("checkout.shippingCity")} *</label>
                <input
                  type="text"
                  id="co-city"
                  value={shipping.shipping_city}
                  onChange={(e) => updateField("shipping_city", e.target.value)}
                  style={inputStyle("shipping_city")}
                />
              </div>
              <div>
                <label htmlFor="co-postal" style={labelStyle}>{t("checkout.shippingPostalCode")} *</label>
                <input
                  type="text"
                  id="co-postal"
                  value={shipping.shipping_postal_code}
                  onChange={(e) => updateField("shipping_postal_code", e.target.value)}
                  style={inputStyle("shipping_postal_code")}
                />
              </div>
            </div>
            <div>
              <label htmlFor="co-country" style={labelStyle}>{t("checkout.shippingCountry")} *</label>
              {/* Paese bloccato: solo aziende tedesche. Read-only; il backend forza
                  comunque "Germany" a prescindere dal valore inviato. */}
              <input
                type="text"
                id="co-country"
                value={t("checkout.germany")}
                readOnly
                aria-readonly="true"
                tabIndex={-1}
                style={{ ...inputStyle("shipping_country"), cursor: "not-allowed", opacity: 0.75 }}
              />
            </div>
            <div>
              <label htmlFor="co-phone" style={labelStyle}>{t("checkout.shippingPhone")} *</label>
              <input
                type="tel"
                id="co-phone"
                value={shipping.shipping_phone}
                onChange={(e) => updateField("shipping_phone", e.target.value)}
                style={inputStyle("shipping_phone")}
              />
            </div>
            <div>
              <label htmlFor="co-notes" style={labelStyle}>{t("checkout.notes")}</label>
              <textarea
                id="co-notes"
                value={shipping.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                rows={3}
                style={{ ...inputStyle("notes"), resize: "vertical" }}
              />
            </div>
          </div>
        </div>

        {/* Order summary */}
        <div style={{
          background: cfg.color_dark,
          borderRadius: 8,
          padding: "28px 24px",
          color: "#fff",
          position: isMobile ? "static" : "sticky",
          top: 96,
        }}>
          <div style={{
            fontSize: 11,
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: 2,
            color: cfg.color_muted,
            marginBottom: 20,
          }}>
            {t("checkout.orderSummary")}
          </div>

          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px 0" }}>
            {items.map((item) => (
              <li key={item.id} style={{
                fontSize: 13,
                padding: "8px 0",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
                <span>
                  {item.title} <span style={{ opacity: 0.5 }}>x{item.quantity}</span>
                </span>
                <span style={{ fontWeight: 500 }}>
                  {formatPrice(item.price_cents * item.quantity)}
                </span>
              </li>
            ))}
          </ul>

          <div style={{
            paddingTop: 12,
            borderTop: "1px solid rgba(255,255,255,0.15)",
            marginBottom: 24,
            display: "flex",
            flexDirection: "column",
            gap: 7,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, opacity: 0.8 }}>
              <span>{t("cart.subtotal")}</span>
              <span>{formatPrice(quote ? quote.subtotal_cents : totalCents)}</span>
            </div>
            {quote && quote.total_discount_cents > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#9fe0b4", fontWeight: 500 }}>
                <span>{t("cart.discount")}</span>
                <span>&minus;{formatPrice(quote.total_discount_cents)}</span>
              </div>
            )}
            {quote && quote.vat_rate_percent > 0 && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, opacity: 0.8 }}>
                  <span>{t("cart.taxableBase")}</span>
                  <span>{formatPrice(quote.taxable_cents)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, opacity: 0.8 }}>
                  <span>{t("cart.vat", { rate: quote.vat_rate_percent })}</span>
                  <span>{formatPrice(quote.vat_cents)}</span>
                </div>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 600, paddingTop: 6 }}>
              <span>{quote && quote.vat_rate_percent > 0 ? t("cart.totalInclVat") : t("checkout.total")}</span>
              <span>{formatPrice(quote ? (quote.vat_rate_percent > 0 ? quote.total_gross_cents : quote.total_cents) : (cfg.vat_rate_percent > 0 ? totalCents + Math.round((totalCents * cfg.vat_rate_percent) / 100) : totalCents))}</span>
            </div>
            <p style={{ fontSize: 10.5, opacity: 0.55, margin: 0, textAlign: "right" }}>
              {t("cart.shippingVatNote")}
            </p>
          </div>

          {/* Codice sconto */}
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="co-discount" style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: cfg.color_muted, marginBottom: 6 }}>
              {t("checkout.discountCode")}
            </label>
            <input
              type="text"
              id="co-discount"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              placeholder={t("checkout.discountCodePh")}
              autoCapitalize="characters"
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(255,255,255,0.06)",
                color: "#fff",
                fontSize: 13,
                outline: "none",
              }}
            />
          </div>

          {config?.mode === "manual" && (
            <p data-testid="manual-notice" style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", marginBottom: 12, lineHeight: 1.5 }}>
              {t("checkout.manualNotice")}
            </p>
          )}

          {error && (
            <p role="alert" style={{ fontSize: 12, color: "#fca5a5", marginBottom: 12 }}>{error}</p>
          )}

          {vatRetryAt !== null && vatRetrySeconds > 0 && (
            <p aria-live="polite" style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", marginBottom: 12 }}>
              {t("checkout.vat.retryIn", { seconds: vatRetrySeconds })}
            </p>
          )}

          {submitting && (
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 12, height: 12, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
              {phase === "payment" ? t("checkout.openingPayment") : t("checkout.vat.verifying")}
            </p>
          )}

          <button
            type="button"
            onClick={handlePlaceOrder}
            disabled={submitting || resuming || items.length === 0 || vatRetrySeconds > 0}
            style={{
              width: "100%",
              background: items.length === 0 ? "rgba(255,255,255,0.1)" : cfg.color_primary,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "12px 24px",
              fontSize: 14,
              fontWeight: 500,
              cursor: items.length === 0 ? "not-allowed" : "pointer",
              opacity: submitting ? 0.7 : 1,
              transition: "opacity 0.2s",
              letterSpacing: 0.3,
            }}
          >
            {submitting
              ? t("checkout.processing")
              : vatRetryAt !== null
                ? t("checkout.vat.retry")
                : config?.mode === "manual"
                  ? t("checkout.placeRequest")
                  : t("checkout.placeOrder")}
          </button>
        </div>
      </div>

      <Link
        to={pathFor("collections", locale)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          marginTop: 40,
          color: cfg.color_muted,
          fontSize: 13,
          textDecoration: "none",
          fontWeight: 500,
          letterSpacing: 0.3,
          transition: "color 0.2s",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
        </svg>
        {t("checkout.backToProducts")}
      </Link>
    </div>
  );
}
